import React, { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { SupportContextProvider } from '../contexts/SupportContext';
import { ChatLauncher } from './chat/ChatLauncher';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

const Layout: React.FC = () => {
  const { isSidebarCollapsed, isMobileSidebarOpen, setMobileSidebarOpen } =
    useStore(
      useShallow(state => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        isMobileSidebarOpen: state.isMobileSidebarOpen,
        setMobileSidebarOpen: state.setMobileSidebarOpen,
      }))
    );
  const mobileDrawerRef = useRef<HTMLDivElement>(null);

  // Mount the drawer's Sidebar only after the first open: a second always-
  // mounted Sidebar duplicates mount effects (e.g. AgencySwitcher's
  // /auth/organizations fetch) on every page load, including desktop.
  const [hasOpenedMobileSidebar, setHasOpenedMobileSidebar] = useState(false);
  useEffect(() => {
    if (isMobileSidebarOpen) {
      setHasOpenedMobileSidebar(true);
    }
  }, [isMobileSidebarOpen]);

  // Lock body scroll while the drawer is open (iOS/Android Safari otherwise
  // scrolls the page under the overlay).
  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileSidebarOpen]);

  // While the drawer is open: move focus into it, close on Escape, and trap Tab
  // focus inside the panel. It's an aria-modal dialog, so focus must not reach
  // the page behind the overlay. A manual trap (rather than the `inert`
  // attribute) keeps this working on older mobile Safari, which lacks `inert`.
  useEffect(() => {
    if (!isMobileSidebarOpen) return;
    const drawer = mobileDrawerRef.current;
    if (!drawer) return;

    const getFocusable = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => el.offsetParent !== null);

    // Move focus into the panel once it can accept it. The just-shown panel can
    // reject focus for a frame or two (and the toggle button that opened it keeps
    // focus otherwise), so retry briefly until focus actually lands inside.
    let focusTimer = 0;
    let attempts = 0;
    const moveFocusIn = () => {
      (getFocusable()[0] ?? drawer).focus();
      if (!drawer.contains(document.activeElement) && attempts++ < 10) {
        focusTimer = window.setTimeout(moveFocusIn, 16);
      }
    };
    focusTimer = window.setTimeout(moveFocusIn, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileSidebarOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        drawer.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        // Wrap backward off the first element (or back in if focus escaped).
        if (active === first || active === drawer || !drawer.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !drawer.contains(active)) {
        // Wrap forward off the last element (or back in if focus escaped).
        e.preventDefault();
        first.focus();
      }
    };
    // Capture phase so the trap runs before nav-item key handlers.
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isMobileSidebarOpen, setMobileSidebarOpen]);

  return (
    <div className='h-screen bg-background'>
      {/* Fixed Sidebar — desktop/tablet */}
      <div
        className={`hidden md:flex fixed inset-y-0 left-0 ${isSidebarCollapsed ? 'w-16' : 'w-52'} z-30 flex-col overflow-hidden border-r bg-background transition-all duration-300`}
      >
        <Sidebar />
      </div>
      {/* Mobile off-canvas sidebar */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${isMobileSidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => setMobileSidebarOpen(false)}
        aria-hidden='true'
      />
      {/* `invisible` when closed keeps the off-canvas panel out of the tab
          order and accessibility tree (React 18 has no `inert` prop);
          visibility is transitioned so the slide-out animation still plays. */}
      <div
        ref={mobileDrawerRef}
        id='mobile-navigation'
        role='dialog'
        aria-modal={isMobileSidebarOpen}
        aria-label='Navigation'
        aria-hidden={!isMobileSidebarOpen}
        tabIndex={-1}
        className={`md:hidden fixed inset-y-0 left-0 z-50 flex w-64 max-w-[85vw] flex-col overflow-hidden border-r bg-background transition-[transform,visibility] duration-300 outline-none ${isMobileSidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}`}
      >
        {(isMobileSidebarOpen || hasOpenedMobileSidebar) && (
          <Sidebar mobile onNavigate={() => setMobileSidebarOpen(false)} />
        )}
      </div>
      {/* Main Content */}
      <div
        className={`${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-52'} flex flex-col transition-all duration-300 h-full`}
      >
        <SupportContextProvider>
          <Header />
          <main className='flex-1 p-2 sm:p-4 md:p-6 overflow-y-auto'>
            <Outlet />
          </main>
        </SupportContextProvider>
        <ChatLauncher />
      </div>
    </div>
  );
};

export default Layout;
